import type { Editor } from "@tiptap/core";
import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { relativePositionToAbsolutePosition, ySyncPluginKey } from "@tiptap/y-tiptap";
import * as Y from "yjs";

export type CommentThread = {
  id: string;
  anchor?: { from?: string; to?: string; quote?: string } | null;
  detached?: boolean;
  resolved?: boolean;
  comments?: Array<{ id: string; text: string; author?: { displayNameSnapshot?: string } }>;
};

function decode(value: string): Y.RelativePosition {
  return Y.decodeRelativePosition(Uint8Array.from(atob(value), (char) => char.charCodeAt(0)));
}

/** Use the live y-prosemirror binding: Yjs text indexes are not editor positions. */
export function resolveCommentRange(editor: Editor, thread: CommentThread): { from: number; to: number } | null {
  if (thread.detached || !thread.anchor?.from || !thread.anchor.to) return null;
  const sync = ySyncPluginKey.getState(editor.state);
  if (!sync) return null;
  try {
    const from = relativePositionToAbsolutePosition(sync.doc, sync.type, decode(thread.anchor.from), sync.binding.mapping);
    const to = relativePositionToAbsolutePosition(sync.doc, sync.type, decode(thread.anchor.to), sync.binding.mapping);
    if (from == null || to == null || from >= to || to > editor.state.doc.content.size) return null;
    return { from, to };
  } catch {
    return null;
  }
}

export function createCommentAnchor(editor: Editor, from: number, to: number) {
  if (from >= to) return null;
  const start = editor.utils.createMappablePosition(from) as ReturnType<typeof editor.utils.createMappablePosition> & { yRelativePosition?: Y.RelativePosition };
  const end = editor.utils.createMappablePosition(to) as typeof start;
  if (!start.yRelativePosition || !end.yRelativePosition) return null;
  const encode = (position: Y.RelativePosition) => btoa(String.fromCharCode(...Y.encodeRelativePosition(position)));
  return {
    from: encode(start.yRelativePosition),
    to: encode(end.yRelativePosition),
    quote: editor.state.doc.textBetween(from, to, " "),
  };
}

export function commentAnchorExtension(
  threads: () => CommentThread[],
  open: (id: string) => void,
  label: () => string,
) {
  return Extension.create({
    name: "collabCommentAnchors",
    addProseMirrorPlugins() {
      const editor = this.editor;
      return [new Plugin({
        props: {
          decorations: () => {
            const decorations: Decoration[] = [];
            for (const thread of threads()) {
              const range = resolveCommentRange(editor, thread);
              if (range && !thread.resolved) {
                decorations.push(Decoration.inline(range.from, range.to, {
                  class: "rounded-sm bg-amber-200/60 dark:bg-amber-700/40 cursor-pointer",
                  "data-comment-thread": thread.id,
                }));
              }
              const position = range?.to ?? editor.state.doc.content.size;
              decorations.push(Decoration.widget(position, () => {
                const chip = document.createElement("button");
                chip.type = "button";
                chip.className = thread.resolved
                  ? "ml-1 inline-flex align-middle rounded-full border bg-muted px-1.5 text-[10px] text-muted-foreground"
                  : "ml-1 inline-flex align-middle rounded-full border border-amber-500/50 bg-amber-100 px-1.5 text-[10px] text-amber-900 dark:bg-amber-900 dark:text-amber-100";
                chip.textContent = `💬 ${thread.comments?.length ?? 0}`;
                chip.title = label();
                chip.setAttribute("aria-label", label());
                chip.setAttribute("data-comment-thread", thread.id);
                chip.addEventListener("mousedown", (event) => event.preventDefault());
                chip.addEventListener("click", (event) => { event.preventDefault(); open(thread.id); });
                return chip;
              }, { side: 1, key: `comment-${thread.id}` }));
            }
            return DecorationSet.create(editor.state.doc, decorations);
          },
          handleClick: (_view, _pos, event) => {
            const target = event.target as HTMLElement;
            const id = target.closest<HTMLElement>("[data-comment-thread]")?.dataset.commentThread;
            if (!id) return false;
            open(id);
            return true;
          },
        },
      })];
    },
  });
}
